import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchAcceptanceInspections } from '../../api/acceptanceInspection';
import { reportError } from '../../utils/notify';
import {
  AcceptanceInspection,
  acceptanceStatusLabel,
} from '../../types/acceptanceInspection';
import { CompanyInventoryItem, formatInventoryQty } from '../../types/companyInventory';
import './ProductionDailyLotPicker.css';

export interface ProductionDailyLotPick {
  acceptanceInspectionId: number;
  inventoryId: number;
  itemName: string;
  specText: string;
  lotNo: string;
  incomingQty: string;
  remainingQty?: string;
}

interface ProductionDailyLotPickerProps {
  inventory: CompanyInventoryItem;
  onClose: () => void;
  onSelect: (pick: ProductionDailyLotPick) => void;
}

function qtyNumber(raw?: string | null): number | null {
  if (raw == null || !String(raw).trim()) return null;
  const cleaned = String(raw)
    .trim()
    .replace(/,/g, '')
    .replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function ProductionDailyLotPicker({ inventory, onClose, onSelect }: ProductionDailyLotPickerProps) {
  const [rows, setRows] = useState<AcceptanceInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const inventoryId = Number(inventory.inventoryId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        setError('품목 정보가 올바르지 않습니다.');
        setRows([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const list = await fetchAcceptanceInspections({ inventoryId });
        if (cancelled) return;
        const filtered = list.filter((row) => Number(row.inventoryId) === inventoryId);
        setRows(
          filtered
            .filter((row) => (row.lotNo || '').trim() || row.inspectionId != null)
            .sort((a, b) => {
              const aRem = qtyNumber(a.remainingQty);
              const bRem = qtyNumber(b.remainingQty);
              const aEmpty = aRem == null || aRem <= 0 ? 1 : 0;
              const bEmpty = bRem == null || bRem <= 0 ? 1 : 0;
              if (aEmpty !== bEmpty) return aEmpty - bEmpty;
              const aOk = a.status === 'APPROVED' ? 0 : 1;
              const bOk = b.status === 'APPROVED' ? 0 : 1;
              if (aOk !== bOk) return aOk - bOk;
              if ((bRem ?? 0) !== (aRem ?? 0)) return (bRem ?? 0) - (aRem ?? 0);
              return (b.inspectionId || 0) - (a.inspectionId || 0);
            })
        );
      } catch {
        if (!cancelled) {
          reportError(setError, '로트 목록을 불러오지 못했습니다.');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [inventoryId]);

  const stockLabel = useMemo(
    () => `${formatInventoryQty(inventory.stockQty)}${inventory.unit?.trim() || ''}`,
    [inventory.stockQty, inventory.unit]
  );

  const availableCount = useMemo(
    () => rows.filter((row) => (qtyNumber(row.remainingQty) ?? 0) > 0).length,
    [rows]
  );

  return createPortal(
    <div className="pdr-lot-picker__backdrop" role="presentation" onClick={onClose}>
      <div
        className="pdr-lot-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdr-lot-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pdr-lot-picker__head">
          <div>
            <p className="pdr-lot-picker__eyebrow">LOT PICKER</p>
            <h3 id="pdr-lot-picker-title">로트 선택</h3>
            <p className="pdr-lot-picker__sub">
              {inventory.itemName}
              {inventory.specText?.trim() ? ` · ${inventory.specText.trim()}` : ''}
              {' · '}현재 재고 {stockLabel}
            </p>
          </div>
          <button type="button" className="pdr-lot-picker__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="pdr-lot-picker__body">
          {loading ? <p className="pdr-lot-picker__hint">불러오는 중...</p> : null}
          {error ? <p className="pdr-lot-picker__error">{error}</p> : null}
          {!loading && !error && rows.length === 0 ? (
            <p className="pdr-lot-picker__hint">
              이 품목에 연결된 인수검사 로트가 없습니다. Lot No.를 직접 입력해도 됩니다.
            </p>
          ) : null}
          {!loading && rows.length > 0 ? (
            <>
              <p className="pdr-lot-picker__count">
                {rows.length}건 · 잔량 있는 Lot {availableCount}건 · 승인 중간검사 투입 기준으로 사용량 계산
              </p>
              <div className="pdr-lot-picker__scroll">
                <table className="pdr-lot-picker__table">
                  <thead>
                    <tr>
                      <th>품명</th>
                      <th>Lot No.</th>
                      <th>입고</th>
                      <th>사용</th>
                      <th>잔량</th>
                      <th>검사일</th>
                      <th>상태</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const remaining = qtyNumber(row.remainingQty);
                      const depleted = remaining != null && remaining <= 0;
                      return (
                        <tr
                          key={row.inspectionId}
                          className={depleted ? 'is-depleted' : undefined}
                        >
                          <td>{row.itemName?.trim() || inventory.itemName || '-'}</td>
                          <td>{row.lotNo?.trim() || '-'}</td>
                          <td>{row.incomingQty?.trim() || '-'}</td>
                          <td>{row.consumedQty?.trim() || '0'}</td>
                          <td className={depleted ? 'is-depleted-qty' : 'is-remain-qty'}>
                            {row.remainingQty?.trim() || '-'}
                          </td>
                          <td>{row.inspectionDate?.slice(0, 10) || '-'}</td>
                          <td>{acceptanceStatusLabel(row.status)}</td>
                          <td>
                            <button
                              type="button"
                              className="pdr-lot-picker__pick"
                              disabled={depleted}
                              title={
                                depleted
                                  ? '잔량이 없습니다'
                                  : '이 Lot을 선택'
                              }
                              onClick={() =>
                                onSelect({
                                  acceptanceInspectionId: row.inspectionId,
                                  inventoryId: inventory.inventoryId,
                                  itemName: row.itemName || inventory.itemName || '',
                                  specText: row.specText || inventory.specText || '',
                                  lotNo: row.lotNo?.trim() || '',
                                  incomingQty: row.incomingQty?.trim() || '',
                                  remainingQty: row.remainingQty?.trim() || '',
                                })
                              }
                            >
                              선택
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        <div className="pdr-lot-picker__actions">
          <button type="button" onClick={onClose}>
            직접 입력
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProductionDailyLotPicker;
